import { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

/**
 * MapPicker - A reusable component for selecting geographic coordinates
 * 
 * Features:
 * - Interactive map with pan and zoom using Leaflet
 * - Click anywhere on map to set location
 * - Current location detection via browser geolocation API
 * - Draggable marker
 * - Manual coordinate input
 * 
 * @param latitude - Current latitude (Y coordinate)
 * @param longitude - Current longitude (X coordinate)
 * @param onLocationChange - Callback fired when location changes
 * @param className - Optional CSS classes for the container
 */

type MapPickerProps = {
  latitude: number | null;
  longitude: number | null;
  onLocationChange: (lat: number, lng: number) => void;
  className?: string;
};

// Fix for default marker icons in webpack/vite
delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

function MapClickHandler({ onLocationChange }: { onLocationChange: (lat: number, lng: number) => void }) {
  useMapEvents({
    click: (e) => {
      onLocationChange(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function MapCenterUpdater({ latitude, longitude }: { latitude: number | null; longitude: number | null }) {
  const map = useMap();
  
  useEffect(() => {
    if (latitude && longitude) {
      map.setView([latitude, longitude], map.getZoom());
    }
  }, [latitude, longitude, map]);
  
  return null;
}

export function MapPicker({ latitude, longitude, onLocationChange, className = '' }: MapPickerProps) {
  const hasLocation = latitude !== null && longitude !== null;
  const center: [number, number] = hasLocation ? [latitude, longitude] : [56.2, 10.15];
  const zoom = hasLocation ? 13 : 6;

  const handleUseCurrentLocation = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          onLocationChange(position.coords.latitude, position.coords.longitude);
        },
        (error) => {
          alert(`Could not get location: ${error.message}`);
        }
      );
    } else {
      alert('Geolocation is not supported by your browser');
    }
  };

  const handleMarkerDrag = (e: L.DragEndEvent) => {
    const marker = e.target;
    const position = marker.getLatLng();
    onLocationChange(position.lat, position.lng);
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleUseCurrentLocation}
          className="px-4 py-2 bg-smartfield text-white rounded-lg hover:bg-smartfield/90 transition-colors"
          title="Use my current location"
        >
          📍 Use Current Location
        </button>
      </div>

      <div className="relative w-full h-80 rounded-lg border-2 border-gray-300 overflow-hidden">
        <MapContainer
          center={center}
          zoom={zoom}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxZoom={20}
            maxNativeZoom={19}
          />
          <MapClickHandler onLocationChange={onLocationChange} />
          <MapCenterUpdater latitude={latitude} longitude={longitude} />
          {hasLocation && (
            <Marker
              position={[latitude, longitude]}
              draggable={true}
              eventHandlers={{
                dragend: handleMarkerDrag,
              }}
            />
          )}
        </MapContainer>
        <div className="absolute top-2 left-2 bg-white/90 px-3 py-2 rounded shadow-md text-sm pointer-events-none z-[1000]">
          <p className="font-medium text-gray-700">Click on the map to set location</p>
        </div>
      </div>

      {hasLocation && (
        <div className="text-sm text-gray-600">
          Selected: {latitude!.toFixed(6)}°, {longitude!.toFixed(6)}°
        </div>
      )}
    </div>
  );
}
